'use client';

import React from 'react';
import { ShieldCheck, ExternalLink, Calendar, FileText, CheckCircle2 } from 'lucide-react';
import { ToolFreshnessProvenance } from '@/lib/freshness/resolver';
import type { ToolTruthPresentation } from '@/lib/external-truth';

interface ToolFreshnessBadgeProps {
  provenance?: ToolFreshnessProvenance | null;
  presentation?: ToolTruthPresentation;
  className?: string;
}

export function ToolFreshnessBadge({ provenance, presentation, className = '' }: ToolFreshnessBadgeProps) {
  const limited = presentation?.restriction === 'reference_only' || presentation?.restriction === 'deferred';
  if ((!provenance || !provenance.hasFreshness || provenance.datasets.length === 0) && !limited) {
    return null; // Pure zero footprint for static tools
  }

  return (
    <div className={`p-4 rounded-xl bg-slate-50 border border-slate-200/90 text-xs text-slate-700 space-y-3 ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-200/60 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-slate-900">
          <ShieldCheck className={`w-4 h-4 shrink-0 ${limited ? 'text-amber-600' : 'text-emerald-600'}`} />
          <span>{limited ? presentation!.heading : 'Căn Cứ Dữ Liệu &amp; Nguồn Tra Cứu Đã Đối Soát'}</span>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 font-semibold text-[11px] border border-emerald-200/80">
          <CheckCircle2 className={`w-3 h-3 ${limited ? 'text-amber-600' : 'text-emerald-600'}`} />
          {limited ? presentation!.statusLabel : 'Đang áp dụng'}
        </span>
      </div>

      {provenance?.datasets?.length ? <div className="space-y-2.5">
        {provenance.datasets.map((ds) => (
          <div key={ds.datasetId} className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1 sm:gap-4 bg-white p-2.5 rounded-lg border border-slate-100 shadow-2xs">
            <div className="space-y-1">
              <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span>{ds.description}</span>
              </div>
              <div className="text-slate-600 text-[11px] flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>
                  <strong>Cơ quan:</strong> {ds.source.authority}
                </span>
                {ds.source.documentNumber && (
                  <span>
                    <strong>Văn bản:</strong> {ds.source.documentNumber}
                  </span>
                )}
                {ds.effectiveFrom && (
                  <span className="flex items-center gap-1 text-slate-500">
                    <Calendar className="w-3 h-3" />
                    Hiệu lực từ: {ds.effectiveFrom}
                  </span>
                )}
              </div>
            </div>

            {ds.source.url && (
              <a
                href={ds.source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium text-[11px] shrink-0 pt-1 sm:pt-0"
              >
                <span>Xem nguồn gốc</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
      </div> : null}

      <div className="text-[11px] text-slate-500 flex items-center justify-between pt-1">
        <span>{limited ? presentation!.notice : '* Dữ liệu được cập nhật tự động và kiểm định đối kháng hiệu lực theo thời gian thực.'}</span>
      </div>
    </div>
  );
}
